/**
 * Data hooks for the Client Success Firms view (ADR-0077).
 *
 *   useClientSuccessFirms() — polls GET /api/firm-success/firms every 30s.
 *     Read-only; the roster tolerates mild staleness (a glance, not a
 *     read-after-write gate), so the poll is slower than the Operations watch
 *     surfaces' 15s. The payload is bounded (tens of firms) so refetching the
 *     whole thing is cheap.
 *
 *   useFirmPause() / useFirmUnpause() — POST /api/firm-success/firms/:id/
 *     {pause,unpause}. On success the matching firm's `routingStatus` in the
 *     firms-list cache is patched directly; no refetch needed (server echoes
 *     the new value). Coverage, counts, and the rest of the panel payload are
 *     unchanged by a global pause.
 *
 *     Cross-tab note: the worker-side Airtable list cache (KV) has a 60s TTL,
 *     so a second tab or another LF-admin session will read the pre-pause
 *     status from cache for up to 60s. The acting tab updates instantly via
 *     the mutation echo. Matches the existing CS read posture (the firm roster
 *     tolerates mild staleness).
 *
 * Spec: engineering/command-center/docs/client-success/phase-1-spec.md (evicted 2026-08-29, #775 round 10)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClientSuccessFirmsResponseSchema,
  FirmPauseResponseSchema,
  FirmDocumentMutationResponseSchema,
  FirmAddressUpdateResponseSchema,
  FirmNameUpdateResponseSchema,
  FirmDesignationUpdateResponseSchema,
  FirmNotificationsEmailUpdateResponseSchema,
  FirmEmailDomainsUpdateResponseSchema,
  FirmRoutingPreferencesUpdateResponseSchema,
  type ClientSuccessFirmsResponse,
  type FirmPauseResponse,
  type FirmDocumentMutationResponse,
  type FirmAddressUpdateResponse,
  type FirmNameUpdateResponse,
  type FirmDesignationUpdateResponse,
  type FirmNotificationsEmailUpdateResponse,
  type FirmEmailDomainsUpdateResponse,
  type FirmRoutingPreferencesUpdateResponse,
  type FirmDesignation,
  type FirmDocumentSlot,
  type FirmDocuments,
  type FirmFile,
} from "@command-center/shared";
import { apiDelete, apiGet, apiPatch, apiPost, apiUpload } from "./api";

const FIRMS_KEY = ["firm-success", "firms"] as const;
const POLL_INTERVAL_MS = 30_000;

export function useClientSuccessFirms() {
  return useQuery<ClientSuccessFirmsResponse, Error>({
    queryKey: FIRMS_KEY,
    queryFn: async () => {
      const raw = await apiGet<unknown>("/api/firm-success/firms");
      const parsed = ClientSuccessFirmsResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/firm-success/firms failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    refetchInterval: POLL_INTERVAL_MS,
  });
}

/**
 * Apply a pause/unpause echo to the cached firms list — patches only the
 * matching firm's `routingStatus`. Returns a new payload if a match was found
 * (so React re-renders), or the input payload unchanged otherwise.
 */
function applyFirmStatus(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmPauseResponse,
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    return { ...f, routingStatus: patch.routingStatus };
  });
  return touched ? { firms: next } : prev;
}

function useFirmRoutingToggle(action: "pause" | "unpause") {
  const queryClient = useQueryClient();
  // #1296: every pause and unpause carries its explanation; the route refuses one without it.
  return useMutation<FirmPauseResponse, Error, { firmId: string; reason: string }>({
    mutationFn: async ({ firmId, reason }) => {
      const raw = await apiPost<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/${action}`,
        { reason },
      );
      const parsed = FirmPauseResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/firm-success/firms/:id/${action} failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmStatus(prev, result),
      );
    },
  });
}

export function useFirmPause() {
  return useFirmRoutingToggle("pause");
}

export function useFirmUnpause() {
  return useFirmRoutingToggle("unpause");
}

/**
 * Slot → key on `ClientSuccessFirm.documents` for the four PDF slots. `logo`
 * is special-cased — it lives at the root of `ClientSuccessFirm` as
 * `logoUrl`, not under `documents`.
 */
const SLOT_DOCUMENT_KEY: Record<
  Exclude<FirmDocumentSlot, "logo">,
  keyof FirmDocuments
> = {
  w9: "w9",
  malpractice: "malpracticeInsurance",
  mfaH: "mfaHandling",
  mfaO: "mfaOriginating",
};

function applyFirmDocument(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmDocumentMutationResponse,
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    if (patch.slot === "logo") {
      return { ...f, logoUrl: patch.file?.url ?? null };
    }
    const key = SLOT_DOCUMENT_KEY[patch.slot];
    return {
      ...f,
      documents: { ...f.documents, [key]: patch.file },
    } satisfies typeof f;
  });
  return touched ? { firms: next } : prev;
}

/**
 * Upload (or replace) a single firm document slot via the content-endpoint
 * worker route. On success the matching firm's `logoUrl` (logo slot) or
 * `documents.<key>` (PDF slots) is patched directly in the firms-list cache;
 * no refetch needed. Errors propagate as the standard api.ts taxonomy
 * (ClientError / ServerError) — caller surfaces in the panel.
 */
export function useFirmDocumentUpload() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmDocumentMutationResponse,
    Error,
    { firmId: string; slot: FirmDocumentSlot; file: File }
  >({
    mutationFn: async ({ firmId, slot, file }) => {
      const raw = await apiUpload<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/documents/${slot}`,
        "file",
        file,
      );
      const parsed = FirmDocumentMutationResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `firm document upload failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmDocument(prev, result),
      );
    },
  });
}

export function useFirmDocumentDelete() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmDocumentMutationResponse,
    Error,
    { firmId: string; slot: FirmDocumentSlot }
  >({
    mutationFn: async ({ firmId, slot }) => {
      const raw = await apiDelete<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/documents/${slot}`,
      );
      const parsed = FirmDocumentMutationResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `firm document delete failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmDocument(prev, result),
      );
    },
  });
}

/**
 * Apply an address-update echo to the cached firms list — patches only the
 * matching firm's `address`. Returns a new payload if a match was found (so
 * React re-renders), or the input payload unchanged otherwise.
 */
function applyFirmAddress(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmAddressUpdateResponse,
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    return { ...f, address: patch.address };
  });
  return touched ? { firms: next } : prev;
}

/**
 * PATCH /api/firm-success/firms/:id/address — set or clear the
 * Originating-side mailing address. Server trims + collapses whitespace-only
 * to null + enforces the 500-char cap and the designation gate; the hook just
 * sends the raw textarea value (or null to clear) and patches the cached
 * firm's `address` on success.
 */
export function useFirmAddressUpdate() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmAddressUpdateResponse,
    Error,
    { firmId: string; address: string | null }
  >({
    mutationFn: async ({ firmId, address }) => {
      const raw = await apiPatch<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/address`,
        { address },
      );
      const parsed = FirmAddressUpdateResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `firm address update failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmAddress(prev, result),
      );
    },
  });
}

// ---------------------------------------------------------------------------
// Firm field edit hooks (ADR-0091 — Firm administration in Command Center)
// ---------------------------------------------------------------------------
// Five PATCH mutation hooks mirroring useFirmAddressUpdate. Each on success
// patches the matching firm's field in the firms-list cache without a refetch
// — the server echoes the post-state, so the cache patch stays in step with
// what a subsequent GET would project.
//
// Designation is the one exception: the cascade affects which document slots
// + sections the panel renders, so the hook patches `designation` AND
// invalidates the firms-list query, forcing a refetch on the next poll cycle
// so any server-side projection differences (none today, but the firm-detail
// projection touches the same fields) reconcile.

function applyFirmNameUpdate(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmNameUpdateResponse,
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    return { ...f, name: patch.name };
  });
  return touched ? { firms: next } : prev;
}

export function useFirmNameUpdate() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmNameUpdateResponse,
    Error,
    { firmId: string; name: string }
  >({
    mutationFn: async ({ firmId, name }) => {
      const raw = await apiPatch<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/name`,
        { name },
      );
      const parsed = FirmNameUpdateResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `firm name update failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmNameUpdate(prev, result),
      );
    },
  });
}

function applyFirmDesignationUpdate(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmDesignationUpdateResponse,
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    return { ...f, designation: patch.designation };
  });
  return touched ? { firms: next } : prev;
}

export function useFirmDesignationUpdate() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmDesignationUpdateResponse,
    Error,
    { firmId: string; designation: FirmDesignation }
  >({
    mutationFn: async ({ firmId, designation }) => {
      const raw = await apiPatch<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/designation`,
        { designation },
      );
      const parsed = FirmDesignationUpdateResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `firm designation update failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmDesignationUpdate(prev, result),
      );
      // Cascade: the new designation may hide/show document slots and the
      // Address section. Invalidate so the next poll reconciles any projection
      // drift; the immediate setQueryData above keeps the badge in sync now.
      queryClient.invalidateQueries({ queryKey: FIRMS_KEY });
    },
  });
}

function applyFirmNotificationsEmailUpdate(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmNotificationsEmailUpdateResponse,
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    return { ...f, notificationsEmail: patch.notificationsEmail };
  });
  return touched ? { firms: next } : prev;
}

export function useFirmNotificationsEmailUpdate() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmNotificationsEmailUpdateResponse,
    Error,
    { firmId: string; notificationsEmail: string | null }
  >({
    mutationFn: async ({ firmId, notificationsEmail }) => {
      const raw = await apiPatch<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/notifications-email`,
        { notificationsEmail },
      );
      const parsed = FirmNotificationsEmailUpdateResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `firm notifications-email update failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmNotificationsEmailUpdate(prev, result),
      );
    },
  });
}

function applyFirmEmailDomainsUpdate(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmEmailDomainsUpdateResponse,
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    return { ...f, emailDomains: patch.emailDomains };
  });
  return touched ? { firms: next } : prev;
}

export function useFirmEmailDomainsUpdate() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmEmailDomainsUpdateResponse,
    Error,
    { firmId: string; emailDomains: string | null }
  >({
    mutationFn: async ({ firmId, emailDomains }) => {
      const raw = await apiPatch<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/email-domains`,
        { emailDomains },
      );
      const parsed = FirmEmailDomainsUpdateResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `firm email-domains update failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmEmailDomainsUpdate(prev, result),
      );
    },
  });
}

function applyFirmRoutingPreferencesUpdate(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmRoutingPreferencesUpdateResponse,
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    return { ...f, routingPreferences: patch.routingPreferences };
  });
  return touched ? { firms: next } : prev;
}

export function useFirmRoutingPreferencesUpdate() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmRoutingPreferencesUpdateResponse,
    Error,
    { firmId: string; routingPreferences: string | null }
  >({
    mutationFn: async ({ firmId, routingPreferences }) => {
      const raw = await apiPatch<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/routing-preferences`,
        { routingPreferences },
      );
      const parsed = FirmRoutingPreferencesUpdateResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `firm routing-preferences update failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyFirmRoutingPreferencesUpdate(prev, result),
      );
    },
  });
}

// Re-export for any panel-only consumers that import from this module.
export type { FirmFile };
